// Peças compartilhadas pelas telas de movimentação (NF, saídas, ajustes, kardex, relatório).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { getEstoqueContexto } from "../../api";
import { AvisoErro, Campo, Modal, btnStyle, formatQtd, inputStyle } from "./ui";

export const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const formatData = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
/** ["AAAA-MM-01", "AAAA-MM-último"] do mês (para links de período). */
export const limitesMesISO = (ano, mes) => {
  const mm = String(mes).padStart(2, "0");
  return [`${ano}-${mm}-01`, `${ano}-${mm}-${String(new Date(ano, mes, 0).getDate()).padStart(2, "0")}`];
};
export const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

export const TIPOS_MOVIMENTO = {
  ABERTURA: { label: "Abertura", cor: "#6366F1" },
  ENTRADA: { label: "Entrada", cor: "#10B981" },
  SAIDA: { label: "Saída", cor: "#EF4444" },
  AJUSTE: { label: "Ajuste", cor: "#F59E0B" },
};

export const MOTIVOS_AJUSTE = [
  { id: "PERDA", label: "Perda" },
  { id: "AVARIA", label: "Avaria" },
  { id: "VENCIMENTO", label: "Vencimento" },
  { id: "ERRO_LANCAMENTO", label: "Erro de lançamento" },
  { id: "CONTAGEM", label: "Diferença de contagem" },
  { id: "OUTRO", label: "Outro" },
];

export function TipoBadge({ tipo, cancelado }) {
  const t = TIPOS_MOVIMENTO[tipo] || { label: tipo, cor: "#888" };
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: `${t.cor}1F`, color: t.cor, textDecoration: cancelado ? "line-through" : "none", whiteSpace: "nowrap" }}>
      {t.label}
    </span>
  );
}

/** Texto legível para os erros estruturados do backend (saldo negativo, divergência, 422). */
export function mensagemErro(e) {
  const d = e?.data;
  if (d && d.codigo === "SALDO_NEGATIVO") {
    const itens = d.itens.map((i) => `${i.produto_nome} (ficaria ${formatQtd(i.saldo_resultante)} em ${formatData(i.data)})`).join("; ");
    return `${d.mensagem}: ${itens}.`;
  }
  if (d && d.codigo === "DIVERGENCIA_NF") return d.mensagem;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" · ");
  return e?.message || "Erro inesperado";
}

export const ehSaldoNegativo = (e) => e?.data?.codigo === "SALDO_NEGATIVO";

export function useContextoEstoque() {
  const [contexto, setContexto] = useState(null);
  const [erro, setErro] = useState("");
  useEffect(() => {
    getEstoqueContexto().then(setContexto).catch((e) => setErro(e.message));
  }, []);
  return { contexto, erro };
}

export function AvisoSemAbertura({ C }) {
  const navigate = useNavigate();
  return (
    <div style={{ background: C.card, border: `1px dashed ${C.border}`, borderRadius: 12, padding: 20, fontSize: 14, color: C.muted, maxWidth: 560 }}>
      As movimentações começam depois da vistoria de abertura.{" "}
      <button onClick={() => navigate("/estoque/vistoria")} style={{ background: "none", border: "none", color: C.brand, fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 14 }}>
        Ir para a vistoria
      </button>
    </div>
  );
}

/** Select nativo agrupado por categoria (bom no celular). `extra(produto)` completa o rótulo. */
export function ProdutoSelect({ C, produtos, value, onChange, extra, style, ...rest }) {
  const grupos = new Map();
  for (const p of produtos) {
    if (!grupos.has(p.categoria_nome)) grupos.set(p.categoria_nome, []);
    grupos.get(p.categoria_nome).push(p);
  }
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)} style={{ ...inputStyle(C), ...style }} {...rest}>
      <option value="">Selecione o produto</option>
      {Array.from(grupos.entries()).map(([categoria, lista]) => (
        <optgroup key={categoria} label={categoria}>
          {lista.map((p) => (
            <option key={p.id} value={p.id}>{p.nome} ({p.unidade_padrao_sigla}){extra ? extra(p) : ""}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/** Bloco de justificativa quando o backend recusou por saldo negativo e o parâmetro permite. */
export function JustificativaSaldoNegativo({ C, erro, valor, onChange }) {
  if (!ehSaldoNegativo(erro) || !erro.data.permitido_com_justificativa) return null;
  return (
    <div style={{ background: `${C.warning}14`, border: `1px solid ${C.warning}`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, fontWeight: 700, color: C.warning, marginBottom: 8 }}>
        <AlertTriangle size={15} /> Saldo ficaria negativo — permitido só com justificativa (fica registrada no log)
      </div>
      <input value={valor} onChange={(e) => onChange(e.target.value)} placeholder="Por que a saída é maior que o saldo?" style={inputStyle(C)} />
    </div>
  );
}

/** Cancelamento com motivo (NF, saída, ajuste). onConfirmar({motivo, justificativa_saldo_negativo}). */
export function ModalCancelar({ C, titulo, descricao, onClose, onConfirmar }) {
  const [motivo, setMotivo] = useState("");
  const [justificativa, setJustificativa] = useState("");
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  async function confirmar() {
    setSalvando(true);
    try {
      await onConfirmar({ motivo: motivo.trim(), justificativa_saldo_negativo: justificativa.trim() || null });
    } catch (e) {
      setErro(e);
      setSalvando(false);
    }
  }

  return (
    <Modal C={C} titulo={titulo} onClose={onClose} largura={460}
      rodape={<>
        <button onClick={onClose} style={btnStyle(C.muted, true)}>Voltar</button>
        <button onClick={confirmar} disabled={salvando || !motivo.trim()} style={{ ...btnStyle(C.danger), opacity: motivo.trim() && !salvando ? 1 : 0.5 }}>
          {salvando ? "Cancelando..." : "Confirmar cancelamento"}
        </button>
      </>}>
      {descricao && <p style={{ fontSize: 13, color: C.muted, marginTop: 0, lineHeight: 1.5 }}>{descricao}</p>}
      <AvisoErro C={C} onClose={() => setErro(null)}>{erro && mensagemErro(erro)}</AvisoErro>
      <JustificativaSaldoNegativo C={C} erro={erro} valor={justificativa} onChange={setJustificativa} />
      <Campo label="Motivo do cancelamento" C={C} obrigatorio>
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} style={inputStyle(C)} autoFocus />
      </Campo>
    </Modal>
  );
}
